import React, { FC, RefObject, useCallback, useMemo, useRef } from "react";
import Fuse from "fuse.js";

import { DataTree, SubCategoriesData, TestData } from "../types";
import { useClickOutside } from "../helpers/use-click-outside";
import { AccordionSideBar } from "./AccordionMenuSection";
import { SubCategoryAccordion } from "./SubCategoryAccordion";
import { MobileSelectMenu } from "./mobile/MobileSelectMenu";
import { AlphabetSelectMenu } from "./mobile/MobileAlphabetMenu";
import { TableItemNoSubCategoryMobile } from "./test-items/TableItemNoSubCategoryMobile";
import { TestItemNoCategoryDesktop } from "./test-items/TestItemNoCategoryDesktop";
import { AlphabetDesktop } from "./test-items/AlphabetDesktop";
import { useSearchStore } from "../stores/search";
import { alphabetArray } from "../constants/alphabet";
import { ResultsWithoutAccordion } from "./ResultsWithoutAccordion";

import * as iuliia from "iuliia";

interface SearchSectionProps {
  data: DataTree[];
  testData: TestData[];
  initialSubCategoryId: string | null;
}

export const SearchTab: FC<SearchSectionProps> = ({
  data,
  testData,
  initialSubCategoryId,
}): JSX.Element => {
  const {
    currentCategoryId,
    setCurrentCategoryId,
    isAlphabetSearch,
    searchQueryInput,
    alphabetSearchCharacter,
    setSearchQueryInput,
  } = useSearchStore((state) => state);

  const searchData = useMemo(() => {
    return testData.map((test) => ({
      ...test,
      name: iuliia.translate(test.name, iuliia.WIKIPEDIA),
    }));
  }, [testData]);

  const fuseSearchStandard = useMemo(
    () =>
      new Fuse(searchData, {
        keys: ["name"],
        minMatchCharLength: 2,
        useExtendedSearch: false,
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [searchData],
  );

  const fuseSearchAlphabet = useMemo(
    () =>
      new Fuse(searchData, {
        keys: ["name"],
        minMatchCharLength: 0,
        threshold: 0,
        useExtendedSearch: true,
        ignoreLocation: false,
        // @ts-ignore
        getFn: (obj, path) => {
          // Use the default `get` function
          const value = Fuse.config.getFn(obj, path);
          // ... do something with `value`
          if (typeof value === "string") {
            // return value.match(/[\w\p{sc=Cyrillic}]+/ug);
            return value;
          }
          return value;
        },
      }),
    [searchData],
  );

  let isInputSearch: boolean = true;

  let results: Fuse.FuseResult<TestData>[] = [];
  let isSearching: boolean = !!searchQueryInput && searchQueryInput.length > 2;

  const setCurrentCategoryName = useSearchStore(
    (state) => state.setCurrentCategoryName,
  );

  if (alphabetSearchCharacter.length > 0 && !isSearching) {
    results = fuseSearchAlphabet.search(
      iuliia.translate(alphabetSearchCharacter, iuliia.WIKIPEDIA),
    );
    isSearching = true;
    isInputSearch = false;
    // setCurrentCategoryName(alphabetSearchCharacter.toUpperCase());
  } else {
    // const transformed = searchQueryInput + '|' + searchQueryInput.split(' ').join('|')
    const transformed = iuliia.translate(searchQueryInput, iuliia.WIKIPEDIA);

    results = fuseSearchStandard.search(transformed as string);
    isInputSearch = isSearching;
    if (searchQueryInput && searchQueryInput?.length > 2) {
      // setCurrentCategoryName(searchQueryInput as string);
    }
  }

  const categoriesMap: { [key: string]: boolean } = results.reduce(
    (acc, curr) => {
      const categoryId = curr.item.categoryId?.length
        ? curr.item.categoryId[0]
        : "";
      return { ...acc, [categoryId]: true };
    },
    {},
  );

  const subCategoriesMap: { [key: string]: boolean } = results.reduce(
    (acc, curr) => {
      const subCategoryId = curr.item.subCategoryId?.length
        ? curr.item.subCategoryId[0]
        : "";
      return { ...acc, [subCategoryId]: true };
    },
    {},
  );

  // @ts-ignore
  const filteredResults: Fuse.FuseResult<TestData>[] = [];

  for (let result of results) {
    if (result.item.categoryId !== "") {
      filteredResults.push(result);
    }
  }

  const testsMap: { [key: string]: boolean } = filteredResults.reduce(
    (acc, curr) => {
      return { ...acc, [curr.item.id]: true };
    },
    {},
  );

  const resultsData = data;
  const clickRef: RefObject<any> = useRef();
  const clickRefAlphabet: RefObject<any> = useRef();

  useClickOutside(clickRef, () => {
    setCurrentCategoryId("");
    setSearchQueryInput("");
    // setAlphabetSearchCharacter('')
    isInputSearch = false;
    // console.log('clicked outside');
    setCurrentCategoryName("Изследвания и цени");
  });

  const titleRef: any = useRef();

  function scrollToDiv() {
    const element = document.getElementById("targetElement");
    const headerOffset = 140;
    const elementPosition = element?.getBoundingClientRect().top;
    const offsetPosition =
      elementPosition && elementPosition + window.scrollY - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }

  const noSubCategories = useCallback((subCategories: SubCategoriesData[]) => {
    if (subCategories) {
      // If it's only 1 subcategory, we assume it doesn't have any subcategories
      return subCategories.length === 1;
    }

    return true;
  }, []);

  return (
    <div className="pricing-wrapper">
      <div className="pricing-list">
        <AlphabetDesktop
          clickRefAlphabet={clickRefAlphabet}
          alphabetArr={alphabetArray}
        />

        <div className="pricing-table-wrapper lg:flex lg:justify-center">
          <div className="pricing-table-nav price-left hidden lg:block">
            <div className="test-link-card price-nav">
              <AccordionSideBar
                scrollToResult={scrollToDiv}
                categoriesData={data}
              />
            </div>
          </div>

          <div className="grid lg:hidden gap-2 mb-4 self-start">
            <MobileSelectMenu categoriesData={data} />
            <AlphabetSelectMenu />
          </div>

          <div
            ref={titleRef}
            id={"targetElement"}
            className="table sizing lg:w-[55%] xl:w-full"
            style={{ paddingTop: 20 }}
          >
            {!results.length && searchQueryInput && (
              <div className="text-black text-center">
                Няма намерени тестове.
              </div>
            )}

            {resultsData.map(
              (category, categoryIndex) =>
                (!isSearching || categoriesMap[category.id]) && (
                  <div
                    id={category.id}
                    key={categoryIndex}
                    className={` main-title-divider
                     ${!isSearching && currentCategoryId && currentCategoryId !== category.id ? "hidden" : ""}`}
                  >
                    {!isInputSearch && !isAlphabetSearch && (
                      <div className="divider-box mt-5">
                        <h4 className="title section-subtitle  left-align category-title">
                          {category.name}
                        </h4>
                      </div>
                    )}

                    {noSubCategories(category.subCategories) && (
                      <div className="sub-category-block">
                        <React.Fragment key={categoryIndex}>
                          {category.subCategories[0].tests.map(
                            (test: any, testIndex) =>
                              !isSearching &&
                              test && (
                                <React.Fragment key={testIndex}>
                                  <TestItemNoCategoryDesktop
                                    test={test}
                                    testIndex={testIndex}
                                  />
                                </React.Fragment>
                              ),
                          )}

                          <div className="tests-cards-grid">
                            {category.subCategories[0].tests.map(
                              (test, testIndex) => {
                                return (
                                  !isSearching &&
                                  test && (
                                    <React.Fragment key={testIndex}>
                                      <TableItemNoSubCategoryMobile
                                        testIndex={testIndex}
                                        test={test}
                                      />
                                    </React.Fragment>
                                  )
                                );
                              },
                            )}
                          </div>
                        </React.Fragment>
                      </div>
                    )}

                    {isInputSearch && (
                      <ResultsWithoutAccordion
                        categoryIndex={categoryIndex}
                        category={category}
                        isSearching={isSearching}
                        subCategoriesMap={subCategoriesMap}
                        testsMap={testsMap}
                      />
                    )}

                    {isAlphabetSearch && (
                      <ResultsWithoutAccordion
                        categoryIndex={categoryIndex}
                        category={category}
                        isSearching={isSearching}
                        subCategoriesMap={subCategoriesMap}
                        testsMap={testsMap}
                      />
                    )}

                    {!isAlphabetSearch &&
                      !isInputSearch &&
                      !noSubCategories(category.subCategories) && (
                        <SubCategoryAccordion
                          initialSubCategoryId={initialSubCategoryId}
                          category={category}
                          isSearching={isSearching}
                          subCategoriesMap={subCategoriesMap}
                          testsMap={testsMap}
                        />
                      )}
                  </div>
                ),
            )}
          </div>
        </div>
      </div>

      <a href="#go-top" className="go-top w-inline-block">
        <div className="go-top-button">
          <div className="go-top-button-bg">
            <div className="go-top-button-icon"></div>
          </div>
        </div>
      </a>
    </div>
  );
};
